import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { ORGANIZATION_USERS_REFERENCE_DATA_TYPES, OrganizationUsersReferenceData } from "@constants/reference-data";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ModalTypes } from "@enums/modal-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { getOrganizationUsersReduxRequest } from "@redux-requests/organization-users";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { extractRelyingPartiesForPropertyName } from "@utils/permissions-util";
import {
    getAllowedAxnAdminUserPropertyBundles,
    getAxnAdminFilteredUsers
} from "@utils/validation/organization/organization-users/organization-users-util";
import { ButtonWithIcon } from "@containers/common/button-with-icon/button-with-icon";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { AddOrganizationUserModal } from "../common/add-organization-user-modal/add-organization-user-modal";
import { OrganizationUsersTableContainer } from "./organization-users-table-container/organization-users-table-container";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { getActiveReferenceData } from "@utils/data-util";
import { getSessionState, getTableSettingsState } from "@redux/selectors/root-selectors";
import { getOrganizationUsersDataState } from "@redux/selectors/organization-users-selectors";

export const OrganizationUsersContainer = () => {
    const modalContext = useModalContext();
    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const { organizationUsers: organizationUsersTableState } = useSelector(getTableSettingsState);
    const {
        isLoadingAxnAdminUsers,
        axnAdminUsers,
        organizationUsersRoles
    } = useSelector(getOrganizationUsersDataState);
    const referenceData = useReferenceDataSelector<OrganizationUsersReferenceData>(ORGANIZATION_USERS_REFERENCE_DATA_TYPES);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadAxnAdminUsers, setShouldLoadAxnAdminUsers] = useState(true);


    useEffect(() => {
        if (!referenceData.isLoading.organizationUsers && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                ORGANIZATION_USERS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.ORGANIZATION_USERS
            );
        }
    }, [referenceData.isLoading.organizationUsers, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.axnIdentityProviders &&
            referenceData.axnRelyingParties &&
            referenceData.axnAdminUserPropertyBundles &&
            referenceData.auth0IdentityProviders &&
            referenceData.documentationLinks &&
            referenceData.axnAdminAvailableUserOrganizationProperties &&
            referenceData.axnAdminAvailableUserRPProperties &&
            referenceData.axnRelyingParties
        );
    }, [
        referenceData.axnCompanies,
        referenceData.axnIdentityProviders,
        referenceData.axnRelyingParties,
        referenceData.axnAdminUserPropertyBundles,
        referenceData.auth0IdentityProviders,
        referenceData.documentationLinks,
        referenceData.axnAdminAvailableUserOrganizationProperties,
        referenceData.axnAdminAvailableUserRPProperties,
        referenceData.axnRelyingParties
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingAxnAdminUsers && shouldLoadAxnAdminUsers) {
            setShouldLoadAxnAdminUsers(false);
            getOrganizationUsersReduxRequest(dispatch)(
                referenceData.axnCompanies,
                referenceData.axnIdentityProviders,
                referenceData.axnAdminUserPropertyBundles,
                referenceData.axnAdminAvailableUserOrganizationProperties,
                referenceData.axnAdminAvailableUserRPProperties,
                referenceData.axnRelyingParties
            );
        }
    }, [isReferenceDataPresent, isLoadingAxnAdminUsers, shouldLoadAxnAdminUsers]);

    const allowedAxnAdminPropertyBundles = useMemo(() => {
        return getAllowedAxnAdminUserPropertyBundles(
            session,
            referenceData.axnRelyingParties,
            referenceData.axnAdminUserPropertyBundles
        );
    }, [session, referenceData.axnRelyingParties, referenceData.axnAdminUserPropertyBundles]);
    
    const {
        axnCompanies: activeAxnCompanies,
        axnRelyingParties: activeAxnRelyingParties
    } = useMemo(() => {
        if (isReferenceDataPresent) {
            const { axnCompanies, axnRelyingParties } = referenceData;

            return getActiveReferenceData({
                axnCompanies,
                axnRelyingParties
            });
        }

        return {
            axnCompanies: [],
            axnRelyingParties: []
        };
    }, [isReferenceDataPresent, referenceData.axnCompanies, referenceData.axnRelyingParties]);

    const renderAddOrganizationUserModal = useMemo(() => (onModalClose: () => void) => {
        const allowedRelyingParties = extractRelyingPartiesForPropertyName(
            UserPermissionTypes.CREATE_USERS,
            session.user.axnAdminUserRPProperties,
            activeAxnRelyingParties
        );

        return (
            <AddOrganizationUserModal
                onModalClose={onModalClose}
                session={session}
                auth0IdentityProviders={referenceData.auth0IdentityProviders}
                axnCompanies={activeAxnCompanies}
                axnAdminUserPropertyBundles={allowedAxnAdminPropertyBundles}
                axnRelyingParties={allowedRelyingParties}
                axnIdentityProviders={referenceData.axnIdentityProviders}
                shouldReloadUsers={true}
                shouldReloadInvitations={false}
                axnAdminAvailableUserOrganizationProperties={referenceData.axnAdminAvailableUserOrganizationProperties}
                axnAdminAvailableUserRPProperties={referenceData.axnAdminAvailableUserRPProperties}
            />
        );
    }, [
        session,
        allowedAxnAdminPropertyBundles,
        referenceData.auth0IdentityProviders,
        activeAxnCompanies,
        activeAxnRelyingParties,
        session.user.axnAdminUserRPProperties,
        referenceData.axnAdminAvailableUserOrganizationProperties,
        referenceData.axnAdminAvailableUserRPProperties
    ]);

    const isLoading = useMemo(() => {
        return isLoadingAxnAdminUsers || referenceData.isLoading.organizationUsers;
    }, [isLoadingAxnAdminUsers, referenceData.isLoading.organizationUsers]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            axnAdminUsers &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData &&
            !shouldLoadAxnAdminUsers
        );
    }, [isLoading, axnAdminUsers, isReferenceDataPresent, shouldLoadReferenceData, shouldLoadAxnAdminUsers]);

    const headerButton = useMemo(() => {
        if (session.userPermissions?.canInviteUsers) {
            const onClick = () => {
                modalContext.setModalState({
                    modalType: ModalTypes.AddOrganizationUser,
                    renderFunction: renderAddOrganizationUserModal
                });
            };
            return (
                <ButtonWithIcon
                    id="add-organization-users-button"
                    title={resources.pages.organizationUsers.headerButton}
                    text={resources.pages.organizationUsers.headerButton}
                    onClick={onClick}
                    icon={faPlusCircle}
                    disabled={isLoading || !isLoaded}
                />
            );
        } else {
            return null;
        }
    }, [
        isLoading,
        isLoaded,
        session.userPermissions?.canInviteUsers,
        renderAddOrganizationUserModal
    ]);

    const axnAdminFilteredUsers = useMemo(() => {
        return getAxnAdminFilteredUsers(axnAdminUsers, allowedAxnAdminPropertyBundles, organizationUsersRoles);
    }, [axnAdminUsers, organizationUsersRoles, allowedAxnAdminPropertyBundles]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader
                title={resources.pages.organizationUsers.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"organization-user-list-page-header"}
                    />
                }
                headerButton={headerButton}
            />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoaded && (
                    <>
                        <OrganizationUsersTableContainer
                            session={session}
                            isPageHeight={true}
                            axnAdminUsers={axnAdminFilteredUsers}
                            axnRelyingParties={referenceData.axnRelyingParties}
                            tableState={organizationUsersTableState}
                        />
                    </>

                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};